import { forwardRef, type ImgHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { BRAND_LOGOS, LOGO_ALT_TEXT } from '@/config/assets';

type LogoVariant = 'full' | 'icon';
type LogoBackground = 'transparent' | 'white';
type LogoPriority = 'eager' | 'lazy';

interface LogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  variant?: LogoVariant;
  bg?: LogoBackground;
  priority?: LogoPriority;
  alt?: string;
}

/**
 * R2 - Smart Logo component with variant selection
 * R3 - Type-safe props with automatic URL resolution
 * R4 - Optimized loading strategies for performance
 * 
 * Usage:
 * <Logo variant="full" bg="transparent" className="h-10" />
 * <Logo variant="icon" bg="white" className="h-8" priority="eager" />
 */
export const Logo = forwardRef<HTMLImageElement, LogoProps>(
  (
    {
      variant = 'full',
      bg = 'transparent',
      priority = 'lazy',
      alt = LOGO_ALT_TEXT,
      className,
      ...props
    },
    ref
  ) => {
    // R3 - Type-safe URL resolution
    const logoUrl = BRAND_LOGOS[variant][bg];

    return (
      <img
        ref={ref}
        src={logoUrl}
        alt={alt}
        loading={priority === 'eager' ? 'eager' : 'lazy'}
        className={cn('object-contain', className)}
        {...props}
      />
    );
  }
);

Logo.displayName = 'Logo';
